import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { PlusIcon } from "@heroicons/react/24/outline";
import React, { useEffect, useState } from "react";
import { aggregateFieldData } from "../api/DataApi";
import { fromJS } from "immutable";
import { useDispatch, useSelector } from "react-redux";
import { formatNumber } from "../api/DataTypeUtils";
import TaskAction from "../actions/TaskAction";

const dataFunctions = (field) => {
  const dataType = field.get("type");

  switch (dataType) {
    case "integer":
    case "double":
      return [
        {
          id: "count_empty",
          title: "Count empty",
        },
        {
          id: "count_filled",
          title: "Count filled",
        },
        {
          id: "percent_empty",
          title: "Percent empty",
        },
        {
          id: "percent_filled",
          title: "Percent filled",
        },
        {
          id: "sum",
          title: "Sum",
        },
        {
          id: "average",
          title: "Average",
        },
        {
          id: "max",
          title: "Max",
        },
        {
          id: "min",
          title: "Min",
        },
      ];
    case "string":
    case "boolean":
    case "guid":
    case "issue":
    case "contact":
    case "account":
      return [
        {
          id: "count_empty",
          title: "Count empty",
        },
        {
          id: "count_filled",
          title: "Count filled",
        },
        {
          id: "percent_empty",
          title: "Percent empty",
        },
        {
          id: "percent_filled",
          title: "Percent filled",
        },
      ];
    default:
      return [];
  }
};

const AggregationFunctions = ({ headers, header, projectId, field }) => {
  const dispatch = useDispatch();

  const filters = useSelector((state: any) => state.taskReducer.getIn(["filters"]));

  const [data, setData] = useState(fromJS({}));

  const handleSelectingFunction = (funcId) => {
    const newSortList = headers
      .toJS()
      .map((item) =>
        item.selector === header.get("selector") ? { ...item, aggregatingFunc: funcId } : item
      );
    dispatch(TaskAction.changeGridFields([...newSortList]));
  };

  const loadAggregations = (funcId) => {
    if (funcId != "None") {
      aggregateFieldData(
        projectId,
        header.get("selector"),
        header.get("formType"),
        funcId,
        filters.toJS()
      ).then((res) => {
        setData(fromJS(res));
      });
    } else {
      setData(fromJS({}));
    }
  };

  useEffect(() => {
    const funcId = header.get("aggregatingFunc") || "None";
    loadAggregations(funcId);
  }, [header.get("aggregatingFunc"), filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const renderValueUnit = () => {
    const funcName = data.get("funcName") || "";
    if (funcName === "percent") {
      return `%`;
    }
    return ``;
  };

  const renderPlaceholder = () => {
    const groupValue = data.get(header.get("selector")) || "None";
    const funcValue = data.get("funcValue") || "";
    if (groupValue === "None") {
      return (
        <div className="flex items-center justify-end w-full h-full px-2 gap-1 text-gray-400">
          <PlusIcon className="w-3.5 h-3.5 shrink-0" />
          <span className="text-sm">Add calculation</span>
        </div>
      );
    }
    const formattedValue = formatNumber(groupValue);
    return (
      <div className="flex items-center justify-end w-full h-full px-2 gap-1">
        <span className="text-sm font-semibold text-gray-600 tabular-nums">
          {formattedValue}
          {renderValueUnit()}
        </span>
        <span className="text-sm text-gray-400 font-normal">{funcValue}</span>
      </div>
    );
  };

  const fieldFunctions = dataFunctions(field);
  if (fieldFunctions.length === 0) {
    return null;
  }

  return (
    <Popover className={"group flex h-full rounded-sm cursor-pointer relative"}>
      <PopoverButton as={"div"} className={"flex w-full h-full hover:bg-gray-100 rounded-sm"}>
        {renderPlaceholder()}
      </PopoverButton>
      <PopoverPanel anchor="top end">
        <div className={"bg-white border border-gray-200 rounded-md shadow-lg"}>
          <div
            className={
              "text-gray-600 border-b border-b-gray-300 h-10 flex items-center align-middle p-2 text-sm"
            }
          >
            Aggregations
          </div>
          {fieldFunctions.map((func) => (
            <div
              className={"text-gray-600 p-2 hover:bg-gray-100 rounded-md text-sm cursor-pointer"}
              key={`${header.get("selector")}_${func.id}`}
              onClick={() => {
                handleSelectingFunction(func.id);
              }}
            >
              {func.title}
            </div>
          ))}
          <div
            className={
              "text-gray-600 border-t border-t-gray-300 h-10 flex items-center align-middle p-2 hover:bg-gray-100 text-sm cursor-pointer"
            }
            onClick={() => {
              handleSelectingFunction("None");
            }}
          >
            None
          </div>
        </div>
      </PopoverPanel>
    </Popover>
  );
};

export default AggregationFunctions;
